import { useState } from 'react';
import { Dialog } from './Dialog.tsx';
import { Icon } from './Icon.tsx';

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * M3 option list for bottom-sheet menus: 48px rows, tonal pill on the chosen
 * one. Shared by SelectField and the filter sheets so every picker in the app
 * looks and behaves the same.
 */
export function OptionList({
  options,
  value,
  onSelect,
}: {
  options: SelectOption[];
  value: string | undefined;
  onSelect: (value: string) => void;
}): JSX.Element {
  return (
    <ul role="listbox" className="-mx-2 flex flex-col gap-0.5">
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <li key={o.value || '__all'}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              className="option-row"
              onClick={() => onSelect(o.value)}
            >
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {selected && <Icon name="check" size={20} />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Drop-in replacement for a native <select>: the trigger looks like the M3
 * outlined `.select` field, and choosing opens an in-app bottom sheet instead
 * of the OS picker (which clashes with the app, especially over another sheet).
 */
export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Choose…',
  disabled,
}: {
  id?: string;
  /** Sheet title — usually the field's visible label. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);

  return (
    <>
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-placeholder={!current}
        className="select select-trigger text-base"
        onClick={() => setOpen(true)}
      >
        <span className="min-w-0 flex-1 truncate">{current?.label ?? placeholder}</span>
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title={label}>
        <OptionList
          options={options}
          value={value}
          onSelect={(v) => { onChange(v); setOpen(false); }}
        />
      </Dialog>
    </>
  );
}
